import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFilters } from './useFilters';
import { getTeamsComparisonRequest, ITeamComparisonItem } from '../services/groupComparison';

interface ICatsList {
  category: {
    code: string;
    title: string;
  }
}

export interface ICatsNames {
    id: string;
    name: string;
    value: string;
}

export const useCompareTeamsSearch = (noInitialGet?: boolean) => {
  const [riskPresent, setRiskPresent] = useState(false);
  const [teamFiltersSelected, setTeamFiltersSelected] = useState<string[]>([]);
  const [riskFiltersSelected, setRiskFiltersSelected] = useState<string[]>([]);
  const [catsNames, setCatsNames] = useState<ICatsNames[]>([]);
  const params = useParams();
  const {applyFilters, currentFilters} = useFilters();
  const { t } = useTranslation();
  const [teamsList, setTeamsList] = useState<ITeamComparisonItem[] | null>(null);

  const getListOnlyTeams = useMemo(() => teamsList?.map((val: ITeamComparisonItem) =>
    ({id: val.team.id, name: val.team.name, workEnvironment: val.workEnvironment})),
  [teamsList]);

  const searchForTeam = async (teamName?: string, initial?: boolean) => {
    const {reportId} = params;
    const filters = {teamName, scope: 'short'};
    if(reportId){
      const teamsList = await getTeamsComparisonRequest(reportId, filters);

      setTeamsList(teamsList?.items);
      if(teamsList?.items.length > 0 && initial) {
        const arrFirstElement = teamsList?.items[0];
        setRiskPresent(!!arrFirstElement?.risk);
        await manageCategoryTypes(arrFirstElement?.workEnvironment, !!arrFirstElement?.kpi, !!arrFirstElement?.risk);
      }
    }
    return 'no report id provieded';
  };

  const sortTeamList = (key: string | undefined, value: string) => {
    if(key){
      const sortVal = JSON.stringify({[key]: value});
      const filterObj = {compareSort: sortVal};
      return applyFilters(filterObj);
    }
  };

  const clearSortOrFilter = (group: string) => {
    const filterObj = {[group]: null};
    return applyFilters(filterObj);
  };

  const clearFilters = (key: string, teamRisk?: boolean) => {
    if(teamRisk){
      setTeamFiltersSelected([]);
      setRiskFiltersSelected([]);
    }
    applyFilters({[key]: null});
  };

  const getCompareFilters = useCallback((key: string) => {
    const groupFilters = currentFilters?.compare || null;
    const parsedGroupFilters = typeof groupFilters === 'string' ? JSON.parse(groupFilters) : null;
    if(parsedGroupFilters) {
      return parsedGroupFilters[key] || null;
    }
  }, [currentFilters]);

  const manageRiskSelection = (val: string) => {
    const isPresentInArr = riskFiltersSelected?.includes(val);
    if(isPresentInArr) {
      const updatedValue = riskFiltersSelected.filter((riskVal: string) => riskVal !== val);
      return setRiskFiltersSelected(updatedValue);
    }
    return setRiskFiltersSelected((prevState: string[]) => ([...prevState, val]));
  };

  const manageTeamSelection = (val: string) => {
    const isPresentInArr = teamFiltersSelected?.includes(val);
    if(isPresentInArr) {
      const updatedValue = teamFiltersSelected.filter((riskVal: string) => riskVal !== val);
      return setTeamFiltersSelected(updatedValue);
    }
    return setTeamFiltersSelected((prevState: string[]) => ([...prevState, val]));
  };

  const selectedItemsCount = useMemo(() =>
    (riskFiltersSelected.length + teamFiltersSelected.length),
  [riskFiltersSelected, teamFiltersSelected]);

  const applyTeamListFilters = () => {
    const compareFilterValue = JSON.stringify(
      {team: teamFiltersSelected, risk: riskFiltersSelected}
    );
    if(selectedItemsCount === 0){
      return applyFilters({compare: null});
    }
    const compareFilterObj = {compare: compareFilterValue, pageNo: '1'};
    return applyFilters(compareFilterObj);
  };

  const clearSortAndFilters = () => {
    applyFilters({compare: null, compareSort: null});
    setTeamFiltersSelected([]);
    setRiskFiltersSelected([]);
  };

  const manageCategoryTypes = (catList: ICatsList[], hasKpi?: boolean, hasRisk?: boolean) => {
    const kpiSortFilter = {
      id: 'fl3',
      name: 'KPI',
      value: 'kpi'
    };

    const riskSortFilter = {
      id: 'fl4',
      name: t('report.groupComparison.sortTypes.risk'),
      value: 'risk',
    };

    const simplifiedCatList = catList.map(({category}: ICatsList) => ({
      id: category?.code,
      name: category?.title,
      value: category?.code,
    }));

    return setCatsNames([
      ...(hasKpi ? [kpiSortFilter] : []),
      ...(hasRisk ? [riskSortFilter] : []),
      ...simplifiedCatList
    ]);
  };

  useEffect(() => {
    const {reportId} = params;
    if(!noInitialGet && reportId){
      (async () => {
        // @ts-expect-error query-string types a value as string | string[]
        const filterObj = currentFilters?.compare ? JSON.parse(currentFilters?.compare) : null;
        if(filterObj){
          setRiskFiltersSelected(filterObj.risk);
          setTeamFiltersSelected(filterObj.team);
        }
        await searchForTeam('', true);
      })();
    }
  }, []);
  return {
    teamsList: getListOnlyTeams,
    searchForTeam,
    clearFilters,
    getCompareFilters,
    sortTeamList,
    clearSortOrFilter,
    riskFiltersSelected,
    manageRiskSelection,
    teamFiltersSelected,
    manageTeamSelection,
    applyTeamListFilters,
    selectedItemsCount,
    clearSortAndFilters,
    catsNames,
    riskPresent,
  };
};
